import { useEffect, useState } from "react";
import { api, type LeadRow } from "../../api";
import { IconDownload } from "../../icons";
import { TabFooter, useEditor } from "../DemoEditor";

const pct = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : "0%");

export default function AnalyticsTab() {
  const { bundle } = useEditor();
  const [days, setDays] = useState(30);
  const [r, setR] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [funnel, setFunnel] = useState(0);

  useEffect(() => {
    setR(null);
    api.analytics(bundle.demo.id, days).then(setR).catch((e) => setError(e.message));
  }, [bundle.demo.id, days]);

  if (error) return <div className="error-box">{error}</div>;

  return (
    <div className="stack">
      <div className="row">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: "auto" }} aria-label="Date range">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={0}>All time</option>
        </select>
        {bundle.demo.status !== "published" && <span className="small muted">This demo is a draft, so it isn't collecting views.</span>}
      </div>
      <LeadsPanel days={days} />
      {!r ? <p className="muted">Loading…</p> : r.totals.views === 0 ? (
        <div className="panel"><p className="muted">No views in this range yet. Share the link from the Publish step, and views appear here as people open it.</p></div>
      ) : (
        <>
          <div className="stats">
            <div className="stat"><b>{r.totals.views}</b><span>Views</span></div>
            <div className="stat"><b>{pct(r.totals.completed, r.totals.views)}</b><span>Finished the tour ({r.totals.completed})</span></div>
            <div className="stat"><b>{r.totals.surveyed}</b><span>Answered the survey</span></div>
          </div>

          {r.daily.length > 1 && (
            <section className="panel stack-s">
              <h3>Views per day</h3>
              <div className="spark" aria-label="Views per day">
                {r.daily.map((d: any) => {
                  const max = Math.max(...r.daily.map((x: any) => x.views));
                  return <i key={d.date} title={`${d.date}: ${d.views}`} style={{ height: `${(d.views / max) * 100}%` }} />;
                })}
              </div>
              <div className="row small muted"><span>{r.daily[0].date}</span><span className="spacer" /><span>{r.daily[r.daily.length - 1].date}</span></div>
            </section>
          )}

          <div className="grid-2">
            <section className="panel stack-s">
              <h3>Outcomes</h3>
              <table className="table">
                <thead><tr><th>Routed to</th><th>Views</th><th>Finished</th></tr></thead>
                <tbody>{r.byOutcome.map((o: any) => <tr key={o.outcomeId ?? "default"}><td>{o.name}</td><td>{o.views}</td><td>{pct(o.completed, o.views)}</td></tr>)}</tbody>
              </table>
            </section>
            <section className="panel stack-s">
              <h3>Sources</h3>
              <table className="table">
                <thead><tr><th>Link tag</th><th>Views</th><th>Finished</th></tr></thead>
                <tbody>{r.bySource.map((s: any) => <tr key={s.source}><td>{s.source}</td><td>{s.views}</td><td>{pct(s.completed, s.views)}</td></tr>)}</tbody>
              </table>
            </section>
          </div>

          {r.funnels.length > 0 && (
            <section className="panel stack-s">
              <div className="row">
                <h3>Where viewers drop off</h3>
                <span className="spacer" />
                {r.funnels.length > 1 && (
                  <select value={funnel} onChange={(e) => setFunnel(Number(e.target.value))} style={{ width: "auto" }} aria-label="Path">
                    {r.funnels.map((f: any, i: number) => <option key={i} value={i}>{f.name}</option>)}
                  </select>
                )}
              </div>
              {(r.funnels[funnel] ?? r.funnels[0]).steps.map((s: any, _i: number, all: any[]) => (
                <div key={s.index} className="bar-row">
                  <span>Screen {s.index + 1}{s.caption ? `: ${s.caption.slice(0, 28)}` : ""}</span>
                  <span className="bar"><i style={{ width: pct(s.reached, all[0].reached || 1) }} /></span>
                  <span className="small">{s.reached} ({pct(s.reached, all[0].reached)})</span>
                </div>
              ))}
            </section>
          )}

          {r.answers.length > 0 && (
            <section className="panel stack">
              <h3>Survey answers</h3>
              {r.answers.map((q: any) => {
                const total = q.options.reduce((n: number, o: any) => n + o.count, 0);
                return (
                  <div key={q.questionId} className="stack-s">
                    <b className="small">{q.prompt}</b>
                    {q.type === "short_text"
                      ? (q.text.length ? q.text.map((t: string, i: number) => <p key={i} className="small">"{t}"</p>) : <p className="small muted">No text answers yet.</p>)
                      : q.options.map((o: any) => (
                        <div key={o.optionId} className="bar-row">
                          <span>{o.label}</span>
                          <span className="bar"><i style={{ width: pct(o.count, total || 1) }} /></span>
                          <span className="small">{o.count}</span>
                        </div>
                      ))}
                  </div>
                );
              })}
            </section>
          )}

          <section className="panel stack-s">
            <h3>Recent views</h3>
            <div style={{ overflowX: "auto" }}>
              <table className="table">
                <thead><tr><th>Started</th><th>Path</th><th>Source</th><th>Progress</th></tr></thead>
                <tbody>
                  {r.recent.map((v: any, i: number) => (
                    <tr key={i}>
                      <td>{new Date(v.startedAt).toLocaleString()}</td>
                      <td>{v.outcome}</td>
                      <td>{v.source || <span className="muted">none</span>}</td>
                      <td>{v.completed ? "Finished" : `${v.reached} of ${v.total}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
      <TabFooter back="preview" />
    </div>
  );
}

function LeadsPanel({ days }: { days: number }) {
  const { bundle } = useEditor();
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  useEffect(() => {
    setLeads(null);
    api.leads(bundle.demo.id, days).then(setLeads).catch(() => setLeads([]));
  }, [bundle.demo.id, days]);
  if (!leads || (leads.length === 0 && bundle.demo.leadGate === "off")) return null;
  return (
    <section className="panel stack-s">
      <div className="row">
        <h3>Leads <span className="muted small">{leads.length}</span></h3>
        <span className="spacer" />
        {leads.length > 0 && <button type="button" className="btn btn-sm" onClick={() => api.downloadLeadsCsv(bundle.demo.id, days).catch((e) => alert(e.message))}><IconDownload />Download CSV</button>}
      </div>
      {leads.length === 0 ? (
        <p className="small muted">No leads in this range yet. The form is {bundle.demo.leadGate === "end" ? "shown before the end card" : bundle.demo.leadGate === "start" ? "shown before the tour" : `shown before screen ${bundle.demo.leadGateStep}`}.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead><tr><th>Email</th><th>Name</th><th>Company</th><th>Source</th><th>Progress</th><th>Captured</th></tr></thead>
            <tbody>
              {leads.slice(0, 200).map((l) => (
                <tr key={l.id}>
                  <td><a href={`mailto:${l.email}`}>{l.email}</a></td>
                  <td>{l.name || <span className="muted">none</span>}</td>
                  <td>{l.company || <span className="muted">none</span>}</td>
                  <td>{l.source || <span className="muted">none</span>}</td>
                  <td>{l.completed ? <span className="pill pill-live">Finished</span> : l.totalSteps ? `${l.reached} of ${l.totalSteps}` : <span className="muted">none</span>}</td>
                  <td className="muted small">{new Date(l.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
